import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { color, font, motion, prefersReducedMotion, size, space, weight } from '../verificationTheme'
import { DEFAULT_PLAYHEAD_RATIO, DEFAULT_WINDOW_SECONDS, stripGeometry, timeToX } from './stripChart'
import type { CompositeSummary } from '../ledger/composite'

// The strip chart: discrete columns marching leftward past a fixed playhead,
// one column per slice of video, each printing its own fingerprint as it
// reaches the playhead. Plan Architecture §6 — a broadcast quality-control
// instrument, not a trading chart.
//
// THE PERFORMANCE CONTRACT, and the reason this file is shaped the way it is.
// Exactly one requestAnimationFrame loop runs, and all it does is write one
// CSS transform onto one element. It never calls setState — a chart that
// re-rendered React 60 times a second on top of a 360° WebGL sphere would cost
// more than the video does. Columns are ordinary React children; they change
// when the ledger changes, roughly once per slice.
//
// Two things do follow the clock without needing a frame: which columns exist
// at all (culling, which `stripGeometry` already gives a slice of margin), and
// which column is under the playhead (the tag). Both are recomputed once a
// second, far away from the frame loop.
//
// HEIGHT CARRIES NO MEANING. Every verified column is the same height. There
// is no magnitude here — a slice either matched the seal or it did not — and a
// varying height would invent a quantity the data does not have. What varies
// is width, and only because a slice's duration really does vary.

// The field, bottom up: a foot below the baseline, the bars themselves, the
// row where a fingerprint is printed, then the ceiling rule. Bars are
// deliberately short — height means nothing here, so a tall bar would spend
// the panel's most valuable space saying nothing.
const FOOT = 20
const BAR_HEIGHT = 64
const TAG_GAP = 8
const TAG_ROW = 16
const CHART_HEIGHT = FOOT + BAR_HEIGHT + TAG_GAP + TAG_ROW + 10

/** Columns carrying a fingerprint: the one under the playhead and the two it
 * has just handed off to, fading as they go. */
const TAG_TRAIL = [1, 0.4, 0.16]

/** Narrower than this and eight characters of hex will not fit over a column,
 * so the tag is dropped rather than drawn on top of its neighbours. */
const MIN_WIDTH_FOR_TAG = 52

/** How far across the strip you can see. Not a zoom on the picture — a change
 * of span, so the columns keep their meaning and simply get thinner. Named in
 * the units a viewer thinks in, not in slices. */
const SPANS: Array<{ seconds: number; label: string }> = [
  { seconds: 30, label: '30 sec' },
  { seconds: 120, label: '2 min' },
  { seconds: 600, label: '10 min' },
]

const keyframes = `
@keyframes forum-vc-print {
  from { transform: scaleY(0); }
  to   { transform: scaleY(1); }
}
@keyframes forum-vc-tag-in {
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
}
`

let injected = false
function ensureStyles() {
  if (injected || typeof document === 'undefined') return
  injected = true
  const style = document.createElement('style')
  style.setAttribute('data-verification-chart', '')
  style.textContent = keyframes
  document.head.appendChild(style)
}

export interface VerificationChartProps {
  summary: CompositeSummary
  /** Ref-backed and stable — the frame loop calls it. Never a value prop. */
  getTime: () => number
  /** Ref-backed and stable. Caps the strip: nothing unfetched is drawn. */
  getBufferedEnd: () => number
  /** Frozen at the mismatch, so a failed chart stops dead where it failed. */
  frozen?: boolean
  /** A phone-width panel: the explanatory caption goes, so the span control
   * keeps a line to itself instead of wrapping to four. */
  narrow?: boolean
}

export default function VerificationChart({
  summary,
  getTime,
  getBufferedEnd,
  frozen = false,
  narrow = false,
}: VerificationChartProps) {
  ensureStyles()

  const frameRef = useRef<HTMLDivElement | null>(null)
  const stripRef = useRef<HTMLDivElement | null>(null)
  const [widthPx, setWidthPx] = useState(0)
  const [windowSeconds, setWindowSeconds] = useState(DEFAULT_WINDOW_SECONDS)
  // Bumped once a second, to re-cull and to move the tag. Never written by the
  // frame loop.
  const [tick, setTick] = useState(0)

  const reducedMotion = prefersReducedMotion()

  useLayoutEffect(() => {
    const frame = frameRef.current
    if (!frame) return undefined
    const measure = () => setWidthPx(frame.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(frame)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (frozen) return undefined
    const timer = window.setInterval(() => setTick((n) => n + 1), 1000)
    return () => window.clearInterval(timer)
  }, [frozen])

  const geometry = useMemo(
    () =>
      stripGeometry({
        summary,
        currentTime: getTime(),
        bufferedEnd: getBufferedEnd(),
        widthPx,
        windowSeconds,
      }),
    // `tick` looks unused and is the point: it is the once-a-second re-cull.
    // getTime/getBufferedEnd are stable by contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary, widthPx, windowSeconds, tick, getTime, getBufferedEnd],
  )

  /** The slice the viewer is actually watching. The fingerprint is printed
   * here, which is the one bold moment in the whole panel. */
  const playheadIndex = useMemo(
    () => {
      const now = getTime()
      const cell = summary.cells.find((c) => c.start <= now && c.end > now)
      return cell ? cell.index : -1
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary, tick, getTime],
  )

  // The one loop. One read, one write, no React.
  useEffect(() => {
    const strip = stripRef.current
    if (!strip || widthPx === 0) return undefined

    const playheadX = widthPx * DEFAULT_PLAYHEAD_RATIO
    const write = (time: number) => {
      strip.style.transform = `translate3d(${playheadX - timeToX(time, windowSeconds, widthPx)}px, 0, 0)`
    }

    if (reducedMotion || frozen) {
      // Stepped, not scrolled: snap to the start of the slice under the
      // playhead, so the strip moves once per cell instead of continuously.
      const now = getTime()
      const cell = summary.cells.find((c) => c.start <= now && c.end > now)
      write(cell ? cell.start : now)
      return undefined
    }

    let frame = requestAnimationFrame(function step() {
      write(getTime())
      frame = requestAnimationFrame(step)
    })
    return () => cancelAnimationFrame(frame)
  }, [widthPx, windowSeconds, reducedMotion, frozen, getTime, summary, tick])

  const { columns, playheadX } = geometry

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: space.md,
          marginBottom: space.sm,
          fontFamily: font.prose,
          fontSize: size.label,
          color: color.bone,
        }}
      >
        <div style={{ opacity: 0.72 }}>{narrow ? '' : 'Each block is one slice of video, checked on its own.'}</div>
        <div style={{ display: 'flex', gap: space.xs, alignItems: 'baseline' }}>
          {SPANS.map((span) => {
            const current = span.seconds === windowSeconds
            return (
              <button
                key={span.seconds}
                type="button"
                data-player-controls
                onClick={() => setWindowSeconds(span.seconds)}
                aria-pressed={current}
                style={{
                  background: current ? color.rule : 'transparent',
                  border: `1px solid ${current ? color.rule : 'transparent'}`,
                  borderRadius: 2,
                  color: color.bone,
                  opacity: current ? 1 : 0.6,
                  cursor: 'pointer',
                  fontFamily: font.prose,
                  fontSize: size.label,
                  padding: '2px 7px',
                }}
              >
                {span.label}
              </button>
            )
          })}
        </div>
      </div>
    <div
      ref={frameRef}
      style={{
        position: 'relative',
        height: CHART_HEIGHT,
        background: color.bed,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* The instrument's rule: a baseline the columns stand on, and a quiet
          ceiling. Not a value axis — there are no values to read off. */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: FOOT, height: 1, background: color.rule }} />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: FOOT + BAR_HEIGHT + TAG_GAP + TAG_ROW,
          height: 1,
          background: color.rule,
          opacity: 0.55,
        }}
      />

      <div ref={stripRef} style={{ position: 'absolute', inset: 0, willChange: 'transform' }}>
        {/* Quality handovers, in their own layer above the columns. */}
        {summary.switches.map((handover) => (
          <div
            key={`${handover.time}-${handover.to}`}
            title={`${handover.from} to ${handover.to}`}
            style={{
              position: 'absolute',
              left: timeToX(handover.time, windowSeconds, widthPx),
              bottom: FOOT,
              width: 1,
              height: BAR_HEIGHT + TAG_GAP,
              background: `repeating-linear-gradient(to bottom, ${color.bone} 0 3px, transparent 3px 7px)`,
              opacity: 0.4,
            }}
          />
        ))}

        {columns.map((column) => {
          const { cell } = column
          const mismatch = cell.state === 'mismatch'
          const checked = cell.state !== 'unchecked'
          // Flush, no gutter: a gap between two columns read as a break in
          // the checking (Gavin, 9 Oct). Each column's own left-to-right fall
          // is what still tells one slice from the next.
          const barWidth = Math.max(2, column.width)
          const trail = playheadIndex - cell.index
          const showTag = cell.hash && column.width >= MIN_WIDTH_FOR_TAG
          const tagOpacity = showTag && trail >= 0 && trail < TAG_TRAIL.length ? TAG_TRAIL[trail] : 0

          return (
            <div
              key={cell.index}
              style={{
                position: 'absolute',
                left: column.x,
                bottom: FOOT,
                width: barWidth,
              }}
            >
              <div
                style={{
                  // EVERY column is full height, checked or not. A slice that
                  // has not been looked at is an EMPTY SLOT, not a short bar:
                  // a short bar reads as a small reading, which would be a
                  // magnitude, and there are no magnitudes here. The row of
                  // slots also shows the shape of the video ahead, so a viewer
                  // can watch each one fill as its bytes are checked.
                  height: BAR_HEIGHT,
                  // A reading, not a slab. A solid rule caps a filled column
                  // and the body falls away beneath it, so a row of these reads
                  // as separate measurements rather than one filled area. A
                  // mismatch is the exception and stays solid: it is the one
                  // thing in this panel allowed to shout.
                  // Checked: a solid reading, capped by a rule. Unchecked: the
                  // same full-height block, HATCHED. Hatching is the oldest
                  // convention there is for "no data here", it survives being
                  // three pixels wide at the widest span, and it cannot be
                  // mistaken for a reading of any size — which a fainter solid
                  // block could be, and was.
                  //
                  // The fall is ACROSS the column, not down it: bright at the
                  // leading edge, falling away to the right. Every column then
                  // carries the same left-to-right sweep the strip itself
                  // travels in, and the row reads as a trace being drawn
                  // rather than a row of blocks sitting still.
                  background: checked
                    ? `linear-gradient(to right, ${mismatch ? color.alarm : color.jade} 0%, transparent 118%)`
                    : `repeating-linear-gradient(45deg, ${color.rule} 0 3px, transparent 3px 6px)`,
                  borderTop: checked
                    ? `2px solid ${mismatch ? color.alarm : color.jade}`
                    : `1px solid ${color.rule}`,
                  // Not yet fetched, so it cannot have been looked at: the same
                  // hatching, set further back.
                  opacity: checked ? (mismatch ? 1 : 0.85) : column.beyondBuffer ? 0.5 : 1,
                  boxSizing: 'border-box',
                  transformOrigin: 'bottom',
                  animation:
                    reducedMotion || !checked
                      ? undefined
                      : `forum-vc-print ${motion.columnPrintMs}ms ease-out`,
                }}
              >
                {/* Checked against more than one sealed manifest. A brighter
                    cap, never a second hue — the same claim, made twice. */}
                {cell.by.length > 1 && <div style={{ height: 3, background: color.bone, opacity: 0.8 }} />}
              </div>

              {tagOpacity > 0 && cell.hash && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: BAR_HEIGHT + TAG_GAP,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontFamily: font.mono,
                    fontSize: size.mono,
                    fontWeight: weight.medium,
                    color: mismatch ? color.alarm : color.bone,
                    opacity: tagOpacity,
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                    // The playhead rule paints over this row, and the moment
                    // the tag is printed is exactly the moment it crosses it.
                    // A chip of the panel ground keeps the hex readable.
                    background: color.ink,
                    padding: '1px 4px',
                    borderRadius: 2,
                    transition: reducedMotion ? undefined : 'opacity 320ms ease',
                    animation: trail === 0 && !reducedMotion ? 'forum-vc-tag-in 200ms ease-out' : undefined,
                  }}
                >
                  {cell.hash.slice(0, 8)}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Everything right of the playhead has been checked but not yet watched:
          verification runs ahead of playback, because bytes are hashed when
          they arrive, not when they play. Dimming the region rather than each
          column keeps the frame loop down to one transform. */}
      <div
        style={{
          position: 'absolute',
          left: playheadX,
          right: 0,
          top: 0,
          bottom: 0,
          background: color.ink,
          opacity: 0.38,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: playheadX,
          top: 8,
          bottom: FOOT - 5,
          width: 1,
          background: color.bone,
          opacity: 0.7,
          pointerEvents: 'none',
        }}
      />
      {/* A foot on the playhead, so the eye lands on it before the columns. */}
      <div
        style={{
          position: 'absolute',
          left: playheadX - 3,
          bottom: FOOT - 8,
          width: 7,
          height: 7,
          background: color.bone,
          opacity: 0.85,
          clipPath: 'polygon(50% 0, 100% 100%, 0 100%)',
          pointerEvents: 'none',
        }}
      />
    </div>
    </div>
  )
}
